import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, Upload, Calendar, Loader2, Check, Wand2,
  Receipt, Shield, Camera, FileText, ArrowLeft, ArrowRight,
  CheckCircle2, Clock, AlertCircle, Undo2,
  Pencil, Trash2, Save, X, RotateCcw, Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';

/**
 * LogPaymentDrawer — v2.4 unified payment flow.
 *
 * 4 steps: Payment → Receipt → Compliance → Review.
 * On Save, atomically:
 *  - Logs the phase payment record
 *  - Uploads receipt(s) (if attached) with optional auto-sum to actual_cost
 *  - Either attaches lien waiver/photos on hand, or fires a compliance
 *    request to the contractor with the right docs bundled
 *  - Marks phase Final + Complete only when user opts in step 4
 *
 * v2.7: Step 1 now carries a "Logged payments" panel — edit/delete any
 * individual payment amount, or "Reset all to $0" to cleanly reopen an item
 * (fixes false Paid / Pay More flags from legacy duplicate/$0 records).
 * Step 2 supports MULTIPLE receipts (new uploads + existing on the item); the
 * payment amount auto-sums across every selected receipt so the lien waiver
 * total matches.
 */
export default function LogPaymentDrawer({
  isOpen,
  onClose,
  phase,
  existingPayments = [],
  onPaymentAdded,
}) {
  const ptype = phase?.phase_type || 'labor';
  // Anything that INVOLVES labor — "Labor" or "Labor & Materials" — runs the
  // full compliance flow (lien waiver, COI, W-9, completion photos). Only pure
  // Material and soft-cost phases get the simple pay + receipt flow.
  const isLabor = ptype === 'labor' || ptype === 'labor_material';
  const isMaterial = ptype === 'material';
  const needsCompliance = isLabor;

  // --- computed: how much is unpaid right now ---
  // v2.4.2: payments are unified in db.contractor_payments. We still do an
  // internal fetch from /api/phases/{id}/payments on open as the canonical
  // source — the `existingPayments` prop is just a hint for first paint.
  // null = not fetched yet (so an empty list after a delete/reset isn't
  // mistaken for "fall back to the stale prop").
  const [fetchedPayments, setFetchedPayments] = useState(null);

  const refreshPayments = useCallback(async () => {
    if (!phase?.id) return;
    try {
      const r = await apiService.getPhasePayments(phase.id);
      const list = Array.isArray(r?.data?.payments) ? r.data.payments : [];
      setFetchedPayments(list);
    } catch (_e) {
      setFetchedPayments([]);
    }
  }, [phase?.id]);

  useEffect(() => {
    if (!isOpen || !phase?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiService.getPhasePayments(phase.id);
        const list = Array.isArray(r?.data?.payments) ? r.data.payments : [];
        if (!cancelled) setFetchedPayments(list);
      } catch (_e) {
        if (!cancelled) setFetchedPayments([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, phase?.id]);

  // Fetch receipts already on this phase so they can be reused in Step 2 —
  // no re-uploading a receipt the user already scanned into Docs.
  useEffect(() => {
    if (!isOpen || !phase?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiService.getPhaseReceipts(phase.id);
        const list = Array.isArray(r?.data?.receipts) ? r.data.receipts : [];
        if (!cancelled) setExistingReceipts(list.filter((x) => x?.category !== 'refund'));
      } catch (_e) {
        if (!cancelled) setExistingReceipts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, phase?.id]);

  // Prefer freshly-fetched (canonical) over the prop; fall back to prop only
  // until the first fetch returns so the first paint isn't blank.
  const livePayments = fetchedPayments !== null ? fetchedPayments : (existingPayments || []);

  const paidSoFar = livePayments.reduce(
    (sum, p) => sum + (parseFloat(p.amount) || 0), 0
  );
  const targetCost = phase?.actual_cost || phase?.estimated_cost || 0;
  const remaining = Math.max(0, targetCost - paidSoFar);

  // --- state ---
  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  // Step 1: Payment
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [notes, setNotes] = useState('');

  // Step 1: per-payment edit / delete / reset
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [rowBusy, setRowBusy] = useState(false);

  // Step 2: Receipts (multiple)
  // newReceipts: files uploaded now — [{ key, file, extracted, extracting }]
  // selectedReceiptIds: ids of receipts already on the item that are reused
  const [newReceipts, setNewReceipts] = useState([]);
  const [selectedReceiptIds, setSelectedReceiptIds] = useState([]);
  const [addReceiptToActual, setAddReceiptToActual] = useState(true);
  const [skipReceipt, setSkipReceipt] = useState(false);
  const [existingReceipts, setExistingReceipts] = useState([]);

  // Step 3: Compliance
  const [complianceMode, setComplianceMode] = useState('request'); // 'request' or 'onhand'
  const [waiverType, setWaiverType] = useState('partial'); // 'partial' or 'final'
  const [lienWaiverFile, setLienWaiverFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);

  // Step 4: Review
  const [markFinal, setMarkFinal] = useState(false);

  // v2.4: contractor doc status — surface W9/COI presence in compliance step
  const [contractorDocs, setContractorDocs] = useState(null);
  const [includeW9, setIncludeW9] = useState(false);
  const [includeCOI, setIncludeCOI] = useState(false);
  const [docStatusLoading, setDocStatusLoading] = useState(false);

  // v2.5.6: Inspection Gate override (HVAC/Electrical/Plumbing/Concrete/Framing)
  // Surfaces inline when the backend returns 422 inspection_required.
  const [inspectionBlocked, setInspectionBlocked] = useState(false);
  const [inspectionOverrideNote, setInspectionOverrideNote] = useState('');
  // v2.5.6: Inline Inspection toggle inside Step 3 — owner can either:
  //   (a) include "Inspection Report" in the docs request emailed to the
  //       contractor (default ON when phase.requires_inspection && !satisfied), or
  //   (b) upload a passing inspection report file right here (PDF or image).
  const inspectionAlreadySatisfied =
    !!phase?.inspection_passed
    || !!phase?.inspection_file_id
    || (Array.isArray(phase?.inspections) && phase.inspections.some(i => i?.status === 'passed'));
  const [includeInspection, setIncludeInspection] = useState(
    !!phase?.requires_inspection && !inspectionAlreadySatisfied
  );
  const [inspectionUploadFile, setInspectionUploadFile] = useState(null);
  const [inspectionUploadName, setInspectionUploadName] = useState('Inspection report');

  // v2.5.8: per-doc "I'll request this from the contractor" toggle inside the
  // "I have them on hand" mode. Lets the owner upload SOME docs themselves
  // and ask the contractor for the rest in a single compliance email.
  const [requestLienWaiverInstead, setRequestLienWaiverInstead] = useState(false);
  const [requestPhotosInstead, setRequestPhotosInstead] = useState(false);
  const [requestInspectionInstead, setRequestInspectionInstead] = useState(false);

  // Fetch contractor doc status whenever the drawer opens for a labor phase
  // with a contractor assigned.
  useEffect(() => {
    if (!isOpen || !isLabor || !phase?.contractor_id) {
      setContractorDocs(null);
      return;
    }
    let cancelled = false;
    setDocStatusLoading(true);
    (async () => {
      try {
        const axios = (await import('axios')).default;
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const r = await axios.get(`${API}/contractors/${phase.contractor_id}/doc-status`);
        if (!cancelled) {
          setContractorDocs(r.data);
          // Default: include W9/COI in request only if missing or expired
          setIncludeW9(!r.data?.w9?.on_file);
          setIncludeCOI(!r.data?.coi?.on_file || r.data?.coi?.expired);
        }
      } catch (e) {
        if (!cancelled) setContractorDocs(null);
      } finally {
        if (!cancelled) setDocStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isLabor, phase?.contractor_id]);

  // Reset when reopened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setAmount(remaining > 0 ? String(remaining.toFixed(2)) : '');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod('check');
      setNotes('');
      setEditingPaymentId(null);
      setEditAmount('');
      setNewReceipts([]);
      setSelectedReceiptIds([]);
      setSkipReceipt(false);
      setAddReceiptToActual(true);
      setComplianceMode(isLabor ? 'request' : 'onhand');
      setWaiverType('partial');
      setLienWaiverFile(null);
      setPhotoFiles([]);
      setMarkFinal(false);
    }
    // Intentionally only depend on isOpen + phase identity — re-initing on
    // every `remaining` change would clobber user input.
  }, [isOpen, phase?.id, isLabor]);

  // --- receipt totals (auto-sum across every selected receipt) ---
  const existingSelected = existingReceipts.filter((r) => selectedReceiptIds.includes(r.id));
  const selectedExistingTotal = existingSelected.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const newReceiptsTotal = newReceipts.reduce((s, r) => s + (parseFloat(r.extracted?.total_amount) || 0), 0);
  const receiptsTotal = selectedExistingTotal + newReceiptsTotal;
  const receiptCount = existingSelected.length + newReceipts.length;
  const anyExtracting = newReceipts.some((r) => r.extracting);

  // Whenever receipts are selected/uploaded, the payment amount follows the
  // combined receipt total — the lien waiver reflects the sum of all receipts.
  useEffect(() => {
    if (receiptCount > 0) {
      setAmount(receiptsTotal.toFixed(2));
    }
  }, [receiptsTotal, receiptCount]);

  const amountNum = parseFloat(amount) || 0;
  const newPaidTotal = paidSoFar + amountNum;
  const willOverpay = amountNum > remaining + 0.01 && remaining > 0;

  // Auto-flip waiverType to 'final' when amount = remaining
  useEffect(() => {
    if (remaining > 0 && Math.abs(amountNum - remaining) < 0.01) {
      setWaiverType('final');
    }
  }, [amountNum, remaining]);

  // --- per-payment edit / delete / reset handlers ---
  const startEditPayment = (p) => {
    setEditingPaymentId(p.id);
    setEditAmount(String(p.amount ?? ''));
  };
  const cancelEditPayment = () => {
    setEditingPaymentId(null);
    setEditAmount('');
  };
  const saveEditPayment = async () => {
    const v = parseFloat(editAmount);
    if (isNaN(v) || v < 0) { toast.error('Enter a valid amount'); return; }
    setRowBusy(true);
    try {
      await apiService.updatePhasePayment(editingPaymentId, { amount: v });
      toast.success('Payment amount updated');
      cancelEditPayment();
      await refreshPayments();
      onPaymentAdded?.({ updated: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update payment');
    } finally {
      setRowBusy(false);
    }
  };
  const deletePayment = async (id) => {
    if (!window.confirm('Delete this payment record? This cannot be undone.')) return;
    setRowBusy(true);
    try {
      await apiService.deletePhasePayment(id);
      toast.success('Payment deleted');
      await refreshPayments();
      onPaymentAdded?.({ deleted: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setRowBusy(false);
    }
  };
  const resetAllPayments = async () => {
    if (!window.confirm(
      'Reset everything paid on this item back to $0?\n\nThis deletes ALL logged payments and reopens the item so you can re-enter it cleanly. This cannot be undone.'
    )) return;
    setRowBusy(true);
    try {
      const r = await apiService.resetPhasePayments(phase.id, {});
      toast.success(`Item reopened — paid reset to $0${r?.data?.deleted_payments ? ` (${r.data.deleted_payments} record(s) cleared)` : ''}`);
      await refreshPayments();
      onPaymentAdded?.({ reset: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset payments');
    } finally {
      setRowBusy(false);
    }
  };

  // --- receipt handlers ---
  const handleReceiptSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSkipReceipt(false);
    const entries = files.map((f) => ({
      key: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      extracted: null,
      extracting: true,
    }));
    setNewReceipts((prev) => [...prev, ...entries]);
    // OCR each file independently so the running total fills in as they return
    for (const entry of entries) {
      try {
        const fd = new FormData();
        fd.append('file', entry.file);
        const r = await apiService.extractReceiptData(fd);
        const x = (r?.data?.success && r.data.extracted_data) ? r.data.extracted_data : {};
        setNewReceipts((prev) => prev.map((it) => it.key === entry.key ? { ...it, extracted: x, extracting: false } : it));
      } catch (_err) {
        setNewReceipts((prev) => prev.map((it) => it.key === entry.key ? { ...it, extracting: false } : it));
      }
    }
    // allow re-selecting the same file again later
    e.target.value = '';
  };
  const removeNewReceipt = (key) => setNewReceipts((prev) => prev.filter((it) => it.key !== key));
  const updateNewReceiptAmount = (key, val) =>
    setNewReceipts((prev) => prev.map((it) => it.key === key
      ? { ...it, extracted: { ...(it.extracted || {}), total_amount: val === '' ? '' : parseFloat(val) } }
      : it));
  const toggleExistingReceipt = (r) => {
    setSkipReceipt(false);
    setSelectedReceiptIds((prev) => prev.includes(r.id) ? prev.filter((id) => id !== r.id) : [...prev, r.id]);
  };

  const goNext = () => {
    if (step === 1 && amountNum <= 0) {
      toast.error('Enter a payment amount');
      return;
    }
    // v2.4: soft costs without requires_compliance skip step 3 entirely.
    let next = step + 1;
    if (next === 3 && !needsCompliance) next = 4;
    setStep(Math.min(4, next));
  };
  const goBack = () => {
    let prev = step - 1;
    if (prev === 3 && !needsCompliance) prev = 2;
    setStep(Math.max(1, prev));
  };

  const handleSave = useCallback(async () => {
    if (amountNum <= 0) {
      toast.error('Enter a payment amount');
      return;
    }
    setIsSaving(true);

    const hasReceipts = newReceipts.length > 0 || selectedReceiptIds.length > 0;

    try {
      // 1. Log payment record
      const paymentRes = await apiService.addPhasePayment(phase.id, {
        amount: amountNum,
        payment_date: paymentDate,
        payment_method: paymentMethod,
        notes,
        receipt_extracted_amount: receiptsTotal || undefined,
        skip_receipt: skipReceipt || !hasReceipts,
        linked_receipt_id: selectedReceiptIds[0] || undefined,
        is_final_payment: markFinal,
        is_additional_payment: phase?.payment_status === 'final',
        // v2.5.6 inspection gate override (only when user explicitly opted in)
        override_inspection: inspectionBlocked && inspectionOverrideNote.trim().length >= 5,
        inspection_override_note: inspectionBlocked && inspectionOverrideNote.trim().length >= 5
          ? inspectionOverrideNote.trim()
          : undefined,
      });

      // 2. Upload each new receipt (with optional auto-add to actual_cost).
      // v2.6.0: the payment already persisted in step 1 — a receipt upload
      // failing (slow/large image, gateway timeout) must NOT nuke the save with
      // a misleading "Failed to save payment". Soft-fail and tell the owner to
      // re-attach from Docs → Receipts.
      let receiptFailCount = 0;
      if (!skipReceipt) {
        for (const entry of newReceipts) {
          try {
            const fd = new FormData();
            fd.append('file', entry.file);
            fd.append('amount', String(entry.extracted?.total_amount ?? ''));
            fd.append('vendor', entry.extracted?.vendor_name || '');
            fd.append('date', entry.extracted?.date || paymentDate);
            fd.append('description', entry.extracted?.description || notes || '');
            fd.append('add_to_actual_cost', addReceiptToActual ? 'true' : 'false');
            // eslint-disable-next-line no-await-in-loop
            await apiService.uploadPhaseReceipt(phase.id, fd);
          } catch (err) {
            console.warn('Receipt upload failed:', err?.response?.data || err);
            receiptFailCount += 1;
          }
        }
      }

      // 3. Compliance — either upload on-hand docs OR fire request
      // v2.4: skip entirely when needsCompliance is false (soft cost without
      // requires_compliance flag — permits, city fees, etc.)
      // v2.5.9: the payment + receipts already persisted above. A compliance
      // doc upload failing (slow/large photo, gateway timeout) must NOT nuke
      // the whole save with a misleading "Failed to save payment" — soft-fail
      // each one and tell the owner exactly what to re-attach.
      const docWarnings = [];
      if (needsCompliance && complianceMode === 'skip') {
        // Owner bypassed compliance — record the skip for the audit trail.
        try {
          await apiService.skipPhaseCompliance(phase.id, {
            user_id: phase?.user_id,
            payment_amount: amountNum,
            note: `Compliance bypassed when logging a ${markFinal ? 'final ' : ''}payment of $${amountNum.toLocaleString()}`,
          });
        } catch (err) {
          console.warn('Skip compliance failed:', err?.response?.data || err);
        }
      } else if (needsCompliance && complianceMode === 'onhand') {
        // Upload lien waiver if provided AND owner is NOT routing it to the
        // contractor for e-sign.
        if (lienWaiverFile && !requestLienWaiverInstead) {
          try {
            const fd = new FormData();
            fd.append('file', lienWaiverFile);
            await apiService.uploadPhaseLienWaiver(phase.id, fd);
          } catch (err) {
            console.warn('Lien waiver upload failed:', err?.response?.data || err);
            docWarnings.push('lien waiver');
          }
        }
        // Upload completion photos if labor + provided + not delegated
        if (isLabor && photoFiles.length > 0 && !requestPhotosInstead) {
          try {
            const fd = new FormData();
            photoFiles.forEach((f) => fd.append('files', f));
            await apiService.uploadPhaseCompletionPhotos(phase.id, fd);
          } catch (err) {
            console.warn('Completion photos upload failed:', err?.response?.data || err);
            docWarnings.push('completion photos');
          }
        }
        // v2.5.6: Upload inspection report if attached + not delegated
        if (inspectionUploadFile && phase?.requires_inspection && !requestInspectionInstead) {
          try {
            const addRes = await apiService.addPhaseInspection(phase.id, {
              name: inspectionUploadName?.trim() || 'Inspection report',
              type: 'other',
            });
            const inspId = addRes?.data?.inspection?.id;
            if (inspId) {
              await apiService.uploadPhaseInspectionFile(phase.id, inspId, inspectionUploadFile);
            }
          } catch (err) {
            console.warn('Inspection upload failed:', err?.response?.data || err);
            toast.warning('Payment saved, but the inspection report failed to upload — try from the inspections badge.');
          }
        }
        // v2.5.8: if the owner flagged any docs to be requested instead, fire
        // a single compliance email asking ONLY for those (the upload-side
        // docs are excluded via the new include_* flags).
        const mixedRequest =
          requestLienWaiverInstead
          || (isLabor && requestPhotosInstead)
          || (phase?.requires_inspection && !inspectionAlreadySatisfied && requestInspectionInstead);
        if (mixedRequest) {
          try {
            await apiService.requestComplianceDocs(phase.id, amountNum, {
              is_partial: waiverType === 'partial',
              total_phase_cost: targetCost,
              already_paid: paidSoFar,
              remaining_after_payment: Math.max(0, remaining - amountNum),
              include_w9: false,
              include_coi: false,
              include_lien_waiver: requestLienWaiverInstead,
              include_photos: isLabor && requestPhotosInstead,
              include_inspection: phase?.requires_inspection && !inspectionAlreadySatisfied && requestInspectionInstead,
            });
          } catch (err) {
            console.warn('Mixed compliance request failed:', err?.response?.data || err);
            toast.warning('Payment saved, but the contractor email for the delegated docs could not be sent. Use the Request button on the phase card.');
          }
        }
      } else if (needsCompliance) {
        // request mode → email contractor
        try {
          await apiService.requestComplianceDocs(phase.id, amountNum, {
            is_partial: waiverType === 'partial',
            total_phase_cost: targetCost,
            already_paid: paidSoFar,
            remaining_after_payment: Math.max(0, remaining - amountNum),
            include_w9: includeW9,
            include_coi: includeCOI,
            include_inspection: includeInspection,
          });
        } catch (err) {
          // Compliance request can fail for many reasons (no contractor, etc.)
          // — don't block the payment save, but warn.
          console.warn('Compliance request failed:', err?.response?.data || err);
          toast.warning('Payment saved, but compliance request could not be sent. Try the Request button on the phase card.');
        }
      }

      toast.success(
        markFinal
          ? `Payment of $${amountNum.toLocaleString()} saved — phase marked complete.`
          : `Payment of $${amountNum.toLocaleString()} saved — $${amountNum.toLocaleString()} marked draw-ready.`
      );
      if (docWarnings.length) {
        toast.warning(
          `Payment saved, but the ${docWarnings.join(' and ')} didn't upload. Add ${docWarnings.length > 1 ? 'them' : 'it'} from the phase's Docs → Compliance.`
        );
      }
      if (receiptFailCount > 0) {
        toast.warning(
          `Payment saved, but ${receiptFailCount} receipt${receiptFailCount > 1 ? 's' : ''} didn't upload. Re-attach ${receiptFailCount > 1 ? 'them' : 'it'} from the phase's Docs → Receipts.`
        );
      }
      onPaymentAdded?.(paymentRes?.data || { amount: amountNum, payment_date: paymentDate });
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.[0]?.msg || 'Failed to save payment');
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  }, [
    amountNum, paymentDate, paymentMethod, notes, receiptsTotal, skipReceipt,
    newReceipts, selectedReceiptIds, addReceiptToActual, markFinal, phase, complianceMode,
    lienWaiverFile, photoFiles, isLabor, waiverType, targetCost, paidSoFar,
    remaining, includeW9, includeCOI, onPaymentAdded, onClose,
    inspectionBlocked, inspectionOverrideNote,
    includeInspection, inspectionUploadFile, inspectionUploadName,
    // v2.5.8 — per-doc opt-in / opt-out toggles
    requestLienWaiverInstead, requestPhotosInstead, requestInspectionInstead,
    inspectionAlreadySatisfied, needsCompliance,
  ]);

  if (!phase) return null;

  const STEPS = [
    { n: 1, label: 'Payment', icon: DollarSign },
    { n: 2, label: 'Receipt', icon: Receipt },
    { n: 3, label: 'Compliance', icon: Shield },
    { n: 4, label: 'Review', icon: CheckCircle2 },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="log-payment-drawer">
        {/* Header */}
        <div className="px-6 pt-6 pb-3 border-b shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">Log Payment — {phase.name}</h2>
          <p className="text-xs text-slate-500 mt-1">
            Phase will stay <b>In progress</b> unless you mark it Final in step 4.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex gap-1 px-3 pt-3 border-b shrink-0">
          {STEPS.map((s) => {
            const isDone = s.n < step;
            const isActive = s.n === step;
            // v2.4: visually mark Compliance as N/A for soft costs without
            // requires_compliance — keeps the user oriented.
            const isSkipped = s.n === 3 && !needsCompliance;
            return (
              <button
                key={s.n}
                onClick={() => s.n < step && !isSkipped && setStep(s.n)}
                disabled={s.n > step || isSkipped}
                className={`flex-1 py-2 px-1 text-center text-[11px] font-bold uppercase tracking-wide border-b-2 transition-colors ${
                  isSkipped ? 'border-transparent text-slate-300 line-through' :
                  isActive ? 'border-violet-600 text-slate-900' :
                  isDone ? 'border-transparent text-emerald-600' :
                  'border-transparent text-slate-400'
                }`}
                data-testid={`payment-step-${s.n}-tab`}
              >
                {s.n} · {isSkipped ? `${s.label} (N/A)` : s.label}
              </button>
            );
          })}
        </div>

        <div className="px-6 py-5 min-h-[200px] flex-1 overflow-y-auto overflow-x-hidden">
          {/* STEP 1: Payment */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Amount paid today</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="payment-amount-input"
                    className="flex-1"
                  />
                  {remaining > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => setAmount(String(remaining.toFixed(2)))}
                      data-testid="pay-full-balance-btn"
                    >
                      Pay full balance
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Remaining unpaid: <b>${remaining.toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
                  {willOverpay && (
                    <span className="text-amber-700 ml-2">⚠ Amount exceeds remaining — will overpay</span>
                  )}
                </p>
              </div>

              <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between text-slate-600">
                  <span>Paid so far</span>
                  <span>${paidSoFar.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>This payment</span>
                  <span className="text-emerald-700">+ ${amountNum.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between font-semibold border-t border-slate-200 pt-1 mt-1">
                  <span>New paid total</span>
                  <span>${newPaidTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* v2.7: Logged payments — edit / delete / reset */}
              {livePayments.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3" data-testid="logged-payments-panel">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                      <Receipt className="w-4 h-4" /> Logged payments ({livePayments.length})
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={resetAllPayments}
                      disabled={rowBusy}
                      data-testid="reset-all-payments-btn"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset all to $0
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-400 mb-2">
                    Fix a wrong amount, remove a duplicate, or reset the whole item back to $0 to reopen it.
                  </p>
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {livePayments.map((p, idx) => (
                      <div key={p.id || idx} className="flex items-center justify-between text-sm bg-slate-50 rounded p-2" data-testid={`logged-payment-${p.id || idx}`}>
                        {editingPaymentId === p.id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <span className="text-slate-500">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={editAmount}
                              onChange={(e) => setEditAmount(e.target.value)}
                              className="w-32 h-7 text-sm"
                              autoFocus
                              data-testid="edit-payment-amount-input"
                            />
                            <span className="text-slate-400 text-xs">{formatDate(p.payment_date)}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={saveEditPayment}
                              disabled={rowBusy}
                              data-testid="save-payment-edit-btn"
                            >
                              {rowBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-slate-500 hover:text-slate-700"
                              onClick={cancelEditPayment}
                              disabled={rowBusy}
                              data-testid="cancel-payment-edit-btn"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-medium">${(parseFloat(p.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                              <span className="text-slate-400 text-xs">{formatDate(p.payment_date)}</span>
                              {p.is_final_payment && (
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Final</Badge>
                              )}
                              {p.payment_method && p.payment_method !== 'legacy_bypass' && (
                                <Badge variant="outline" className="text-[10px]">{p.payment_method}</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                                onClick={() => startEditPayment(p)}
                                disabled={rowBusy}
                                title="Edit amount paid"
                                data-testid={`edit-payment-btn-${p.id || idx}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => deletePayment(p.id)}
                                disabled={rowBusy}
                                title="Delete payment"
                                data-testid={`delete-payment-btn-${p.id || idx}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Payment method</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="wire">Wire transfer</SelectItem>
                      <SelectItem value="ach">Stripe ACH</SelectItem>
                      <SelectItem value="card">Credit card</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment date</Label>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1">{formatDate(paymentDate)}</p>
                </div>
              </div>

              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything to remember about this payment…"
                />
              </div>
            </div>
          )}

          {/* STEP 2: Receipts (multiple) */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Running total across every selected receipt */}
              {receiptCount > 0 && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between" data-testid="receipts-total-banner">
                  <div className="text-sm text-emerald-900">
                    <span className="font-semibold">{receiptCount}</span> receipt{receiptCount === 1 ? '' : 's'} selected
                    {anyExtracting && <span className="ml-2 text-xs text-emerald-700 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> scanning…</span>}
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-wide text-emerald-700">Combined total</div>
                    <div className="font-bold text-emerald-900" data-testid="receipts-combined-total">${receiptsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                </div>
              )}

              {existingReceipts.length > 0 && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3" data-testid="existing-receipts-picker">
                  <div className="flex items-center gap-2 mb-1"><Receipt className="w-4 h-4 text-violet-600" /><span className="text-sm font-semibold text-violet-900">Receipts already on this item</span><Badge variant="secondary" className="text-[10px]">{existingReceipts.length}</Badge></div>
                  <p className="text-xs text-slate-500 mb-2">Tick any you already uploaded — their amounts add to the total. No need to scan them again.</p>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {existingReceipts.map((r) => {
                      const sel = selectedReceiptIds.includes(r.id);
                      return (
                        <button type="button" key={r.id} data-testid={`existing-receipt-${r.id}`} onClick={() => toggleExistingReceipt(r)} className={`w-full text-left rounded-lg border p-2.5 transition-colors ${sel ? 'border-violet-500 bg-white ring-1 ring-violet-300' : 'border-slate-200 bg-white hover:border-violet-300'}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-emerald-700 text-sm">${(parseFloat(r.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            {sel ? <CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0" /> : <span className="text-[10px] text-violet-600 font-medium">Add</span>}
                          </div>
                          {(r.vendor || r.vendor_name) && <div className="text-xs text-slate-700 truncate">{r.vendor || r.vendor_name}</div>}
                          <div className="text-[11px] text-slate-400 truncate">{r.date ? formatDate(r.date) : ''}{r.description ? ` · ${r.description}` : ''}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-2">Or upload more receipts below.</div>
                </div>
              )}

              <div>
                <Label>Attach receipt(s) or invoice(s)</Label>
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.heic,.heif"
                  multiple
                  onChange={handleReceiptSelect}
                  data-testid="receipt-file-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">Pick several at once — each is scanned and its amount added to the total.</p>
              </div>

              {/* Uploaded receipts list with editable amounts */}
              {newReceipts.length > 0 && (
                <div className="space-y-2" data-testid="new-receipts-list">
                  {newReceipts.map((entry) => (
                    <div key={entry.key} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-2.5" data-testid={`new-receipt-${entry.key}`}>
                      <Wand2 className="w-4 h-4 text-violet-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-800 truncate">{entry.file.name}</div>
                        <div className="text-[11px] text-slate-400 truncate">
                          {entry.extracting ? 'Scanning with AI…' : (entry.extracted?.vendor_name || 'No vendor detected')}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-slate-500 text-sm">$</span>
                        {entry.extracting ? (
                          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                        ) : (
                          <Input
                            type="number"
                            step="0.01"
                            value={entry.extracted?.total_amount ?? ''}
                            onChange={(e) => updateNewReceiptAmount(entry.key, e.target.value)}
                            className="w-24 h-8 text-sm"
                            placeholder="0.00"
                            data-testid={`new-receipt-amount-${entry.key}`}
                          />
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => removeNewReceipt(entry.key)}
                          title="Remove"
                          data-testid={`remove-new-receipt-${entry.key}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {newReceipts.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-3">
                  <Checkbox
                    checked={addReceiptToActual}
                    onCheckedChange={(v) => setAddReceiptToActual(!!v)}
                    data-testid="add-to-actual-cost-checkbox"
                  />
                  <label className="text-sm flex-1 cursor-pointer" onClick={() => setAddReceiptToActual(!addReceiptToActual)}>
                    Add these receipts&apos; amounts to phase Actual Cost
                    <span className="block text-xs text-slate-500 mt-0.5">Recommended for accurate draw tracking</span>
                  </label>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2 border-t">
                <Checkbox
                  checked={skipReceipt}
                  onCheckedChange={(v) => {
                    setSkipReceipt(!!v);
                    if (v) { setNewReceipts([]); setSelectedReceiptIds([]); }
                  }}
                  data-testid="skip-receipt-checkbox"
                />
                <label className="text-sm cursor-pointer" onClick={() => setSkipReceipt(!skipReceipt)}>
                  No receipt for this payment (record only)
                </label>
              </div>
            </div>
          )}

          {/* STEP 3: Compliance */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label>Phase type</Label>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                  {isLabor && <span className="font-medium text-slate-900">Labor — requires lien waiver + completion photos per payment</span>}
                  {isMaterial && <span className="font-medium text-slate-900">Material — requires lien waiver only</span>}
                  {!isLabor && !isMaterial && <span className="font-medium text-slate-900">Soft cost — lien waiver only</span>}
                </div>
              </div>

              <div>
                <Label>Lien waiver type</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setWaiverType('partial')}
                    className={`p-2 rounded-md text-sm font-medium border transition-colors ${
                      waiverType === 'partial' ? 'bg-blue-50 border-blue-400 text-blue-900' : 'bg-white border-slate-200 text-slate-700'
                    }`}
                    data-testid="waiver-type-partial-btn"
                  >
                    Conditional Partial
                  </button>
                  <button
                    type="button"
                    onClick={() => setWaiverType('final')}
                    className={`p-2 rounded-md text-sm font-medium border transition-colors ${
                      waiverType === 'final' ? 'bg-emerald-50 border-emerald-400 text-emerald-900' : 'bg-white border-slate-200 text-slate-700'
                    }`}
                    data-testid="waiver-type-final-btn"
                  >
                    Unconditional Final
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {waiverType === 'partial'
                    ? 'Use for any payment that is not the closeout.'
                    : 'Use only if this is the FINAL payment. Auto-suggested when amount = remaining.'}
                </p>
              </div>

              <div>
                <Label>How will you get these docs?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setComplianceMode('request')}
                    className={`p-3 rounded-md text-sm font-medium border transition-colors text-left ${
                      complianceMode === 'request' ? 'bg-violet-50 border-violet-400 text-violet-900' : 'bg-white border-slate-200 text-slate-700'
                    }`}
                    data-testid="compliance-mode-request-btn"
                  >
                    <div className="font-semibold">Request from contractor</div>
                    <div className="text-xs opacity-80 mt-1">Email with tokenized link (no login needed)</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setComplianceMode('onhand')}
                    className={`p-3 rounded-md text-sm font-medium border transition-colors text-left ${
                      complianceMode === 'onhand' ? 'bg-emerald-50 border-emerald-400 text-emerald-900' : 'bg-white border-slate-200 text-slate-700'
                    }`}
                    data-testid="compliance-mode-onhand-btn"
                  >
                    <div className="font-semibold">I have them on hand</div>
                    <div className="text-xs opacity-80 mt-1">Upload PDFs/photos now</div>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setComplianceMode('skip')}
                  className={`mt-2 w-full p-3 rounded-md text-sm font-medium border transition-colors text-left ${
                    complianceMode === 'skip' ? 'bg-amber-50 border-amber-400 text-amber-900' : 'bg-white border-slate-200 text-slate-700'
                  }`}
                  data-testid="compliance-mode-skip-btn"
                >
                  <div className="font-semibold">Skip compliance &amp; just pay</div>
                  <div className="text-xs opacity-80 mt-1">
                    Record the payment now without a lien waiver. We&apos;ll note it was skipped — you can still request the waiver later from the phase card.
                  </div>
                </button>
              </div>

              {complianceMode === 'onhand' && (
                <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-[11px] text-slate-600 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                    <strong>Mix &amp; match:</strong> upload the docs you have, check {'"Request from contractor instead"'} for the ones you don{"'"}t. The contractor will get a single email asking only for the missing items.
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Signed lien waiver (PDF)</Label>
                      <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
                        <Checkbox
                          checked={requestLienWaiverInstead}
                          onCheckedChange={(v) => {
                            setRequestLienWaiverInstead(!!v);
                            if (v) setLienWaiverFile(null);
                          }}
                          data-testid="request-lien-waiver-instead"
                        />
                        Request from contractor instead
                      </label>
                    </div>
                    {!requestLienWaiverInstead ? (
                      <>
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => setLienWaiverFile(e.target.files?.[0] || null)}
                          data-testid="lien-waiver-file-input"
                        />
                        {lienWaiverFile && <p className="text-xs text-slate-500 mt-1">{lienWaiverFile.name}</p>}
                      </>
                    ) : (
                      <p className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1">
                        The contractor will be emailed to e-sign the {waiverType === 'final' ? 'Unconditional Final' : 'Conditional Partial'} waiver for ${amountNum.toLocaleString(undefined, { minimumFractionDigits: 2 })}.
                      </p>
                    )}
                  </div>

                  {isLabor && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="flex items-center gap-1">
                          <Camera className="w-3 h-3" /> Completion photos (multiple)
                        </Label>
                        <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
                          <Checkbox
                            checked={requestPhotosInstead}
                            onCheckedChange={(v) => {
                              setRequestPhotosInstead(!!v);
                              if (v) setPhotoFiles([]);
                            }}
                            data-testid="request-photos-instead"
                          />
                          Request from contractor instead
                        </label>
                      </div>
                      {!requestPhotosInstead ? (
                        <>
                          <Input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
                            multiple
                            onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))}
                            data-testid="photo-files-input"
                          />
                          {photoFiles.length > 0 && (
                            <p className="text-xs text-slate-500 mt-1">{photoFiles.length} photo(s) selected</p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1">
                          The contractor will be emailed to upload completion photos.
                        </p>
                      )}
                    </div>
                  )}

                  {/* v2.5.6: Inline inspection upload for inspection-required trades */}
                  {phase?.requires_inspection && !inspectionAlreadySatisfied && (
                    <div className="border-t border-slate-200 pt-3" data-testid="onhand-inspection-section">
                      <div className="flex items-center justify-between mb-1">
                        <Label className="flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Inspection report
                          <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded ml-1">required</span>
                        </Label>
                        <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
                          <Checkbox
                            checked={requestInspectionInstead}
                            onCheckedChange={(v) => {
                              setRequestInspectionInstead(!!v);
                              if (v) setInspectionUploadFile(null);
                            }}
                            data-testid="request-inspection-instead"
                          />
                          Request from contractor instead
                        </label>
                      </div>
                      {!requestInspectionInstead ? (
                        <>
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              type="text"
                              placeholder='Label · e.g. "Rough Framing"'
                              className="h-9 text-sm flex-1"
                              value={inspectionUploadName}
                              onChange={(e) => setInspectionUploadName(e.target.value)}
                              data-testid="inspection-upload-name-input"
                            />
                          </div>
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                            className="mt-2"
                            onChange={(e) => setInspectionUploadFile(e.target.files?.[0] || null)}
                            data-testid="inspection-upload-file-input"
                          />
                          {inspectionUploadFile && (
                            <p className="text-xs text-emerald-700 mt-1">{inspectionUploadFile.name} · saves as a passed inspection on this phase</p>
                          )}
                          <p className="text-[11px] text-slate-500 mt-1">
                            PDF or photo of the passing inspection. Saves alongside the payment so the gate unblocks for future payments too.
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1">
                          The contractor will be emailed to upload the passing inspection report.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {complianceMode === 'request' && (
                <>
                  {/* v2.4: contractor W9 / COI status check — show what's on file
                      and let the user include them in the request if missing. */}
                  {isLabor && phase?.contractor_id && (
                    <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">
                        Contractor docs on file
                      </div>
                      {docStatusLoading && (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <Loader2 className="w-3 h-3 animate-spin" /> Checking…
                        </div>
                      )}
                      {!docStatusLoading && contractorDocs && (
                        <>
                          {/* W9 row */}
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              {contractorDocs.w9?.on_file ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-amber-600" />
                              )}
                              <span className="font-medium">W-9</span>
                              {contractorDocs.w9?.on_file ? (
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-300">on file</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300">missing</Badge>
                              )}
                            </div>
                            {!contractorDocs.w9?.on_file && (
                              <label className="flex items-center gap-1 text-xs cursor-pointer">
                                <Checkbox
                                  checked={includeW9}
                                  onCheckedChange={(v) => setIncludeW9(!!v)}
                                  data-testid="include-w9-checkbox"
                                />
                                <span>Request</span>
                              </label>
                            )}
                          </div>
                          {/* COI row */}
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              {contractorDocs.coi?.on_file && !contractorDocs.coi?.expired ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-amber-600" />
                              )}
                              <span className="font-medium">COI (Insurance)</span>
                              {contractorDocs.coi?.on_file && !contractorDocs.coi?.expired ? (
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-300">
                                  {contractorDocs.coi?.expiration ? `valid · expires ${formatDate(contractorDocs.coi.expiration)}` : 'on file'}
                                </Badge>
                              ) : contractorDocs.coi?.expired ? (
                                <Badge variant="outline" className="text-[10px] bg-red-50 text-red-800 border-red-300">expired</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300">missing</Badge>
                              )}
                            </div>
                            {(!contractorDocs.coi?.on_file || contractorDocs.coi?.expired) && (
                              <label className="flex items-center gap-1 text-xs cursor-pointer">
                                <Checkbox
                                  checked={includeCOI}
                                  onCheckedChange={(v) => setIncludeCOI(!!v)}
                                  data-testid="include-coi-checkbox"
                                />
                                <span>Request</span>
                              </label>
                            )}
                          </div>
                          {/* v2.5.6: Inspection row (HVAC/Electrical/Plumbing/Concrete/Framing) */}
                          {phase?.requires_inspection && (
                            <div className="flex items-center justify-between text-sm" data-testid="compliance-inspection-row">
                              <div className="flex items-center gap-2">
                                {inspectionAlreadySatisfied ? (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                ) : (
                                  <AlertCircle className="w-4 h-4 text-amber-600" />
                                )}
                                <span className="font-medium">Inspection report</span>
                                {inspectionAlreadySatisfied ? (
                                  <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-300">on file</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300">missing</Badge>
                                )}
                              </div>
                              {!inspectionAlreadySatisfied && (
                                <label className="flex items-center gap-1 text-xs cursor-pointer">
                                  <Checkbox
                                    checked={includeInspection}
                                    onCheckedChange={(v) => setIncludeInspection(!!v)}
                                    data-testid="include-inspection-checkbox"
                                  />
                                  <span>Request</span>
                                </label>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {!docStatusLoading && !contractorDocs && (
                        <p className="text-xs text-slate-500">Unable to check contractor doc status.</p>
                      )}
                    </div>
                  )}

                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm">
                    <div className="font-semibold text-violet-900 mb-1">Contractor will receive</div>
                    <ul className="space-y-1 text-violet-800">
                      <li className="flex items-center gap-2">
                        <Check className="w-3 h-3" /> Lien waiver ({waiverType === 'final' ? 'Unconditional Final' : 'Conditional Partial'}) — auto-filled for ${amountNum.toLocaleString()}
                      </li>
                      {isLabor && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3" /> Completion photos request (2+ angles)
                        </li>
                      )}
                      {includeW9 && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3" /> W-9 (currently missing)
                        </li>
                      )}
                      {includeCOI && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3" /> COI / Certificate of Insurance{contractorDocs?.coi?.expired ? ' (renewal — current one expired)' : ' (currently missing)'}
                        </li>
                      )}
                      {phase?.requires_inspection && includeInspection && !inspectionAlreadySatisfied && (
                        <li className="flex items-center gap-2" data-testid="contractor-will-receive-inspection">
                          <Check className="w-3 h-3" /> Inspection report (PDF or photo)
                        </li>
                      )}
                    </ul>
                    <p className="text-xs text-violet-700 mt-2 opacity-90">Phase flips to ✅ Draw-ready when all items return.</p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 4: Review */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <Label>Mark this phase as Final?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setMarkFinal(false)}
                    className={`p-3 rounded-md text-sm font-medium border transition-colors ${
                      !markFinal ? 'bg-slate-100 border-slate-400 text-slate-900' : 'bg-white border-slate-200 text-slate-600'
                    }`}
                    data-testid="mark-final-no-btn"
                  >
                    <div className="font-semibold">No · keep In progress</div>
                    <div className="text-xs opacity-80 mt-1">More payments expected</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMarkFinal(true)}
                    className={`p-3 rounded-md text-sm font-medium border transition-colors ${
                      markFinal ? 'bg-emerald-50 border-emerald-500 text-emerald-900' : 'bg-white border-slate-200 text-slate-600'
                    }`}
                    data-testid="mark-final-yes-btn"
                  >
                    <div className="font-semibold">Yes · Mark Complete</div>
                    <div className="text-xs opacity-80 mt-1">This is the closeout</div>
                  </button>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                <div className="text-xs font-bold tracking-wide uppercase text-emerald-700 mb-1">Ready to save</div>
                <Row label="Payment" value={`$${amountNum.toLocaleString(undefined, { minimumFractionDigits: 2 })} · ${paymentMethod}`} />
                <Row label="Date" value={formatDate(paymentDate)} />
                {skipReceipt || receiptCount === 0
                  ? <Row label="Receipt" value="Skipped" muted />
                  : <Row label="Receipts" value={`${receiptCount} · $${receiptsTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}${newReceipts.length > 0 && addReceiptToActual ? ' · added to Actual Cost' : ''}`} />}
                {needsCompliance && (
                  <Row
                    label="Lien waiver"
                    value={`${waiverType === 'final' ? 'Unconditional Final' : 'Conditional Partial'} · ${complianceMode === 'request' ? 'will request from contractor' : (lienWaiverFile ? lienWaiverFile.name : 'not attached')}`}
                  />
                )}
                {isLabor && (
                  <Row
                    label="Completion photos"
                    value={complianceMode === 'request' ? 'will request from contractor' : (photoFiles.length > 0 ? `${photoFiles.length} attached` : 'not attached')}
                  />
                )}
                <Row label="Draw-ready stamp" value={`$${amountNum.toLocaleString()} added to draw-eligible`} />
                <Row
                  label="Phase status"
                  value={markFinal ? 'Complete (closed)' : 'In progress (kept open)'}
                  highlight={markFinal}
                />
              </div>

              {/* v2.5.6: Inspection Gate override banner (shown only after backend blocks) */}
              {inspectionBlocked && (
                <div
                  className="bg-red-50 border border-red-300 rounded-lg p-4 space-y-2"
                  data-testid="inspection-override-banner"
                >
                  <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
                    <AlertCircle className="w-4 h-4" /> Inspection report required
                  </div>
                  <p className="text-xs text-red-900">
                    This trade (HVAC, Electrical, Plumbing, Concrete, or Framing) needs a
                    passing inspection report before payment is recorded. Upload it from the
                    phase row (recommended) — or provide a written override reason below to
                    push the payment through and create an audit trail.
                  </p>
                  <Label className="text-xs">Override reason (≥ 5 chars)</Label>
                  <Textarea
                    value={inspectionOverrideNote}
                    onChange={(e) => setInspectionOverrideNote(e.target.value)}
                    rows={2}
                    placeholder="e.g. Inspector verbally confirmed pass — report being mailed."
                    data-testid="inspection-override-note-input-drawer"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-slate-50 shrink-0">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={step === 1 || isSaving}
            data-testid="payment-back-btn"
            className="mr-auto"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isSaving}
            data-testid="payment-cancel-btn"
          >
            Cancel
          </Button>
          {step < 4 ? (
            <Button onClick={goNext} disabled={isSaving} data-testid="payment-continue-btn" className="shrink-0">
              Continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={isSaving || amountNum <= 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="payment-save-btn"
            >
              {isSaving ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
              ) : (
                <>Save Payment &amp; Mark Draw-Ready</>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, muted, highlight }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-slate-600 flex items-center gap-1">
        <Check className="w-3 h-3 text-emerald-600" /> {label}
      </span>
      <span className={`font-semibold ${muted ? 'text-slate-400 font-normal' : highlight ? 'text-emerald-700' : 'text-slate-900'}`}>
        {value}
      </span>
    </div>
  );
}
